import { displayName } from "~/common/functions";
import type { ChangeRequestsBoolExp, ChangeRequestsOrderBy, OrderBy } from "~/common/types/graphql";
import type { GetChangeRequestsQueryVariables } from "~/features/changeRequests/gqls";
import type {
  ChangeRequestDetailRaw,
  ChangeRequestDetailUI,
  ChangeRequestItemRaw,
  ChangeRequestItemUI,
  ChangeRequestListFilters,
  ChangeRequestRaw,
  ChangeRequestRow,
  ChangeRequestSortOrder,
  ChangeRequestSummary,
  ChangeRequestSummaryProp,
  SortDirection,
} from "~/features/changeRequests/types";
import type { Locale } from "~/i18n/resolveLocale";

const HEADLINE_KEYS = ["title", "event_title", "name"] as const;
const EXCLUDED_KEYS = new Set(["id", "categoryIDs", "evidenceIDs", "deactivatedAt", "sourceIDs", "targetIDs"]);

const isUuid = (value: unknown): boolean =>
  typeof value === "string" && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

const isDomainId = (value: unknown): boolean =>
  typeof value === "object" &&
  value !== null &&
  "value" in value &&
  typeof (value as { value: unknown }).value === "string";

const truncate = (str: string, maxLen: number): string =>
  str.length > maxLen ? `${str.slice(0, maxLen - 3)}...` : str;

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null ? (value as Record<string, unknown>) : {};

/** Condense a change request's items into the row summary (V1 ChangeRequestSummaryCell parity). */
function extractSummary(
  items: ChangeRequestRaw["items"],
  categoryLabelById: Map<string, string>,
): ChangeRequestSummary {
  const empty: ChangeRequestSummary = {
    modelType: "",
    modelTypeLabel: "",
    operationType: "",
    categoryNames: "",
    headline: "",
    secondaryProps: [],
  };
  if (items.length === 0) return empty;

  // Prefer an entity/relation item over recordMetadata for the headline.
  const primary = items.find((i) => i.modelType === "entity" || i.modelType === "relation") ?? items[0];
  const modelType = primary.modelType;
  const operationType = primary.operationType;

  const categoryIds: string[] = [];
  for (const item of items) {
    if (item.modelType !== "entity" && item.modelType !== "relation") continue;
    const cids = asRecord(item.properties).categoryIDs;
    if (Array.isArray(cids)) {
      for (const cid of cids) {
        const value = (cid as { value?: unknown }).value;
        if (typeof value === "string") categoryIds.push(value);
      }
    }
  }
  const categoryNames = categoryIds
    .map((id) => categoryLabelById.get(id) ?? "")
    .filter(Boolean)
    .join(" → ");

  let headline = "";
  const secondaryProps: ChangeRequestSummaryProp[] = [];

  if (modelType === "recordMetadata") {
    const data = asRecord(asRecord(primary.properties).data);
    const entries = Object.entries(data);
    headline = entries[0]?.[0] ?? "";
    for (const [key, value] of entries.slice(0, 3)) {
      if (typeof value === "string" && !isUuid(value)) secondaryProps.push({ key, value: truncate(String(value), 40) });
    }
  } else {
    const props = asRecord(asRecord(primary.properties).properties);
    for (const key of HEADLINE_KEYS) {
      const value = props[key];
      if (typeof value === "string") {
        headline = value;
        break;
      }
    }
    for (const [key, value] of Object.entries(props)) {
      if (secondaryProps.length >= 3) break;
      if (HEADLINE_KEYS.includes(key as (typeof HEADLINE_KEYS)[number])) continue;
      if (EXCLUDED_KEYS.has(key)) continue;
      if (isDomainId(value) || isUuid(value)) continue;
      if (value === null || value === undefined) continue;
      const displayValue = typeof value === "object" ? JSON.stringify(value) : String(value);
      secondaryProps.push({ key, value: truncate(displayValue, 40) });
    }
  }

  return {
    modelType,
    modelTypeLabel: modelType === "recordMetadata" ? "metadata" : modelType,
    operationType,
    categoryNames,
    headline,
    secondaryProps,
  };
}

function toChangeRequestRow(raw: ChangeRequestRaw, categoryLabelById: Map<string, string>): ChangeRequestRow {
  const def = raw.changeRequestRecordComposerDefinition?.recordComposerDefinition ?? null;
  return {
    id: raw.id,
    status: raw.statusId,
    createdAt: raw.createdAt,
    commentsCount: raw.commentsAggregate.aggregate?.count ?? 0,
    composer: def
      ? {
          id: def.id,
          name: def.name,
          kind: def.entityComposerDefinition ? "entity" : def.relationComposerDefinition ? "relation" : null,
          dipId: def.dataImportProjectRecordComposerDefinition?.dataImportProjectId ?? null,
        }
      : null,
    job: raw.batchChangeRequestsJob?.name
      ? {
          id: raw.batchChangeRequestsJob.id,
          name: raw.batchChangeRequestsJob.name,
          dipId: raw.batchChangeRequestsJob.dataImportProjectId ?? null,
        }
      : null,
    jobRunAt: raw.batchChangeRequestsJobRun?.createdAt ?? null,
    summary: extractSummary(raw.items, categoryLabelById),
  };
}

export function toChangeRequestRowList(
  raws: readonly ChangeRequestRaw[],
  categoryLabelById: Map<string, string>,
): ChangeRequestRow[] {
  return raws.map((raw) => toChangeRequestRow(raw, categoryLabelById));
}

const uniqueById = <T extends { id: string }>(items: T[]): T[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
};

/** Proposed category ids, or `null` when the change request omits `categoryIDs` entirely.
 *  Omitted means "no category change" (V1 parity); an explicit `[]` means "remove all". */
const proposedCategoryIds = (properties: unknown): string[] | null => {
  const cids = asRecord(properties).categoryIDs;
  if (!Array.isArray(cids)) return null;
  return [
    ...new Set(cids.map((c) => (c as { value?: unknown }).value).filter((v): v is string => typeof v === "string")),
  ];
};

const sameSet = (a: string[], b: string[]): boolean => a.length === b.length && a.every((x) => b.includes(x));

const googleSheetUrl = (spreadsheetId: string | number, sheetId: string | number): string =>
  `https://docs.google.com/spreadsheets/d/${spreadsheetId}/edit#gid=${sheetId}`;

/** Build the old (current record) vs new (proposed) values fed to the diff viewer (V1 parity). */
function extractDiff(item: ChangeRequestItemRaw): { diffOld: unknown; diffNew: unknown } {
  const crProps = asRecord(item.properties);
  if (item.modelType === "recordMetadata") {
    const rm = item.changeRequestItemRecordMetadata?.recordMetadata;
    return {
      diffNew: { data: crProps.data, deactivatedAt: crProps.deactivatedAt ?? null },
      diffOld: { data: rm?.data ?? {}, deactivatedAt: rm?.deactivatedAt ?? null },
    };
  }
  const crMeta = asRecord(crProps.metadata);
  const rec = item.changeRequestItemRecord?.record;
  return {
    diffNew: {
      properties: crProps.properties,
      metadata: "data" in crMeta ? { data: crMeta.data } : undefined,
      deactivatedAt: crProps.deactivatedAt ?? null,
    },
    diffOld: {
      properties: rec?.properties ?? {},
      metadata: rec ? { data: rec.recordMetadata?.data } : undefined,
      deactivatedAt: rec?.deactivatedAt ?? null,
    },
  };
}

/** The model's own editable properties, shown in the item's JSON tab. */
function itemModelProps(item: ChangeRequestItemRaw): Record<string, unknown> {
  const props = asRecord(item.properties);
  return item.modelType === "recordMetadata" ? asRecord(props.data) : asRecord(props.properties);
}

function toChangeRequestItemUI(item: ChangeRequestItemRaw, locale: Locale): ChangeRequestItemUI {
  const isMetadata = item.modelType === "recordMetadata";
  const originalCategoryIds = [
    ...new Set(
      isMetadata
        ? (item.changeRequestItemRecordMetadata?.recordMetadata?.recordMetadataCategories.map((c) => c.categoryId) ??
            [])
        : (item.changeRequestItemRecord?.record?.recordMetadata.recordMetadataCategories.map((c) => c.categoryId) ??
            []),
    ),
  ];
  const proposed = proposedCategoryIds(item.properties);
  const categoryPresent = proposed !== null;
  const categoryIds = proposed ?? [];

  const recordId = isMetadata ? null : (item.changeRequestItemRecord?.recordId ?? null);
  const recordLink = item.modelType === "entity" ? "entity" : item.modelType === "relation" ? "relation" : null;
  const recordMetadataId = isMetadata
    ? (item.changeRequestItemRecordMetadata?.recordMetadataId ??
      item.changeRequestItemRecordMetadata?.recordMetadata?.id ??
      null)
    : null;

  const deactivatedAt = asRecord(item.properties).deactivatedAt;

  return {
    id: item.id,
    modelType: item.modelType,
    modelTypeLabel: isMetadata ? "metadata" : item.modelType,
    operationType: item.operationType,
    recordId,
    recordLink,
    recordName: displayName(item.changeRequestItemRecord?.record, locale),
    recordMetadataId,
    deactivatedAt: typeof deactivatedAt === "string" ? deactivatedAt : null,
    categoryPresent,
    categoryIds,
    originalCategoryIds,
    categoryChanged: categoryPresent && !sameSet(originalCategoryIds, categoryIds),
    modelProps: itemModelProps(item),
    ...extractDiff(item),
  };
}

export function toChangeRequestDetailUI(raw: ChangeRequestDetailRaw, locale: Locale): ChangeRequestDetailUI {
  const run = raw.batchChangeRequestsJobRun;
  const runJob = run?.batchChangeRequestsJob ?? null;

  // Flatten the job → composer-definition → evidence-set → batch-evidences-job → data-source chain.
  const batchEvidencesJobs = (runJob?.batchChangeRequestsJobRecordComposerDefinitions ?? []).flatMap((d) =>
    d.recordComposerDefinition.recordComposerDefinitionEvidenceSets.flatMap((s) => s.evidenceSet.batchEvidencesJobs),
  );
  const dataSourcesById = new Map<string, ChangeRequestDetailUI["source"]["dataSources"][number]>();
  for (const job of batchEvidencesJobs) {
    const ds = job.dataSource;
    if (ds && !dataSourcesById.has(ds.id)) {
      dataSourcesById.set(ds.id, {
        id: ds.id,
        name: ds.name,
        gsheetUrl: ds.dataSourceGSheets
          ? googleSheetUrl(ds.dataSourceGSheets.googleSpreadsheetId, ds.dataSourceGSheets.googleSheetId)
          : null,
      });
    }
  }

  return {
    id: raw.id,
    status: raw.statusId,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    description: raw.description,
    context: raw.context,
    composerKey: raw.composerKey ?? null,
    batchChangeRequestsJobId: raw.batchChangeRequestsJobId ?? raw.batchChangeRequestsJob?.id ?? null,
    userName: raw.user?.displayName ?? raw.user?.email ?? "—",
    evidences: uniqueById(raw.evidenceChangeRequests.map((e) => ({ id: e.evidence.id }))),
    source: {
      dataImportProject: runJob?.dataImportProject ?? null,
      dataSources: [...dataSourcesById.values()],
      batchEvidencesJobs: uniqueById(batchEvidencesJobs.map((j) => ({ id: j.id, name: j.name ?? null }))),
      batchChangeRequestsJob: runJob ? { id: runJob.id, name: runJob.name ?? null } : null,
      jobRunId: run?.id ?? null,
    },
    items: raw.items.map((item) => toChangeRequestItemUI(item, locale)),
    reviews: raw.reviews.map((r) => ({
      id: r.id,
      typeId: r.typeId,
      comment: r.comment ?? "",
      createdAt: r.createdAt,
      userName: r.user?.displayName ?? r.user?.email ?? "—",
    })),
    commit: raw.commit
      ? {
          createdAt: raw.commit.createdAt,
          items: raw.commit.items.map((i) => ({
            changeRequestItemId: i.changeRequestItemId,
            properties: i.properties,
            createdAt: i.createdAt,
          })),
        }
      : null,
  };
}

/** Translate the UI filter shape into a Hasura `ChangeRequestsBoolExp`. Ported from V1. */
function buildChangeRequestsWhere(filters: ChangeRequestListFilters): ChangeRequestsBoolExp {
  const andList: ChangeRequestsBoolExp[] = [];

  if (filters.statuses.length > 0) andList.push({ statusId: { _in: filters.statuses } });
  if (filters.modelTypes.length > 0) andList.push({ items: { modelType: { _in: filters.modelTypes } } });
  if (filters.hasReviewComments) {
    andList.push({
      reviewsAggregate: { count: { filter: { typeId: { _eq: "comment" } }, predicate: { _gt: 0 } } },
    });
  }
  if (filters.categoryIds.length > 0) {
    andList.push({
      _or: filters.categoryIds.map((id) => ({
        items: { properties: { _cast: { String: { _iregex: `"categoryIDs".*"value"\\s*:\\s*"${id}"` } } } },
      })),
    });
  }
  if (filters.dataImportProjectIds.length > 0) {
    andList.push({
      batchChangeRequestsJobRun: {
        batchChangeRequestsJob: { dataImportProjectId: { _in: filters.dataImportProjectIds } },
      },
    });
  }
  if (filters.batchChangeRequestsJobId) {
    andList.push({ batchChangeRequestsJobId: { _eq: filters.batchChangeRequestsJobId } });
  }
  if (filters.batchChangeRequestsJobRunId) {
    andList.push({ batchChangeRequestsJobRunId: { _eq: filters.batchChangeRequestsJobRunId } });
  }
  if (filters.recordComposerDefinitionId) {
    andList.push({
      changeRequestRecordComposerDefinition: {
        recordComposerDefinitionId: { _eq: filters.recordComposerDefinitionId },
      },
    });
  }
  if (filters.search) {
    andList.push({
      items: {
        properties: {
          _cast: { String: { _iregex: `"(title|event_title|name|action)"\\s*:\\s*"[^"]*${filters.search}[^"]*"` } },
        },
      },
    });
  }

  return { _and: andList };
}

const orderValue = (direction: SortDirection): OrderBy => (direction === "asc" ? "ASC" : "DESC");

function buildChangeRequestsOrderBy(
  sortOrder: ChangeRequestSortOrder,
  direction: SortDirection,
): ChangeRequestsOrderBy {
  const dir = orderValue(direction);
  switch (sortOrder) {
    case "status":
      return { statusId: dir };
    case "comments":
      return { reviewsAggregate: { count: dir } };
    default:
      return { [sortOrder]: dir };
  }
}

export function buildChangeRequestsVariables(args: {
  tenantId: string;
  filters: ChangeRequestListFilters;
  sortOrder: ChangeRequestSortOrder;
  direction: SortDirection;
  pageIndex: number;
  pageSize: number;
}): GetChangeRequestsQueryVariables {
  const { tenantId, filters, sortOrder, direction, pageIndex, pageSize } = args;
  return {
    tenantId,
    limit: pageSize,
    offset: pageIndex * pageSize,
    where: buildChangeRequestsWhere(filters),
    order_by: buildChangeRequestsOrderBy(sortOrder, direction),
  };
}
